import type { GameState, GridCell, Milestone, ServiceType } from '../types/core';
import { cellKey } from '../utils/grid';
import { hashString, mulberry32 } from '../utils/random';

export const WORLD_SIZE = 24;

const initialServicesByMilestone: Record<Milestone, readonly ServiceType[]> = {
  Hamlet: [],
  Village: ['school', 'utilities', 'bus'],
  Town: ['police', 'fire', 'hospital', 'landmarks'],
  'Large Town': ['train', 'entertainment'],
  City: ['airport'],
  Metropolis: []
};

export function createInitialCells(size = WORLD_SIZE): GridCell[] {
  const rng = mulberry32(hashString('the-bees-hive-terrain'));
  const cells: GridCell[] = [];

  for (let x = 0; x < size; x += 1) {
    for (let z = 0; z < size; z += 1) {
      const coord = { x, z };
      const distanceFromCenter = Math.hypot(x - size / 2, z - size / 2) / size;
      const height = Math.sin(x * 0.7) * 0.025 + Math.cos(z * 0.55) * 0.018 - distanceFromCenter * 0.02;

      cells.push({
        key: cellKey(coord),
        coord,
        terrainHeight: Number(height.toFixed(4)),
        treeSeed: rng(),
        flowerSeed: rng(),
        road: x === 0 || z === 0 || x === size - 1 || z === size - 1,
        roadEdge: x === 0 || z === 0 || x === size - 1 || z === size - 1
      });
    }
  }

  return cells;
}

export function servicesForMilestone(milestone: Milestone): readonly ServiceType[] {
  const order: readonly Milestone[] = ['Hamlet', 'Village', 'Town', 'Large Town', 'City', 'Metropolis'];
  const index = order.indexOf(milestone);
  const services = new Set<ServiceType>();

  for (const item of order.slice(0, index + 1)) {
    for (const service of initialServicesByMilestone[item]) {
      services.add(service);
    }
  }

  return Array.from(services);
}

export function createInitialGameState(): GameState {
  return {
    hydrated: false,
    hasSeenWelcome: false,
    selectedTool: 'residential',
    hover: {},
    world: {
      size: WORLD_SIZE,
      cells: createInitialCells(),
      buildings: [],
      citizens: [],
      vehicles: []
    },
    time: {
      day: 1,
      minuteOfDay: 7 * 60,
      speed: 2,
      paused: false
    },
    economy: {
      credits: 4800,
      incomePerDay: 0,
      expensesPerDay: 0,
      tourism: 0,
      maintenance: 0
    },
    progression: {
      milestone: 'Hamlet',
      unlockedServices: servicesForMilestone('Hamlet'),
      nextMilestonePopulation: 25
    },
    weather: {
      kind: 'sunshine',
      intensity: 0,
      cloudCover: 0.18,
      nextChangeMinute: 20 * 60
    },
    camera: {
      target: { x: 0, y: 0, z: 0 },
      rotation: Math.PI / 4,
      zoom: 24
    },
    walk: {
      active: false,
      position: { x: 0, z: 0 },
      heading: Math.PI
    }
  };
}
